import CircularLoader from 'components/CircularLoader';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import MatterIndicators from 'components/matters/MatterIndicators';
import { ModalFooter } from 'components/modals/ModalFooter';
import { ModalHeader } from 'components/modals/ModalHeader';
import useAuth from 'hooks/useAuth';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useCloseCaseMutation, useGetCaseClosingCheckQuery } from 'store/api/matters';
import { CaseClosingCheckDto, CloseCaseXrChoice, ConfidentialityDecision, isClosingRefusal } from 'types/caseClosing';
import { MatterDto } from 'types/matters';
import { successAlert } from 'utils/alert';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { t } from 'utils/getTranslations';
import { isMatterInProgress } from 'utils/matterState';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import {
    buildCloseMatterRequest,
    buildXrTaskPrefill,
    CloseCaseFormValues,
    CloseCaseTaskPrefillState,
    isCaseClosingBlocked,
    isCloseCaseButtonDisabled,
} from './closeCaseModalState';
import { ConfidentialitySection } from './ConfidentialitySection';
import { isConfidentialityChoiceValid, requiresStillConfidentialConfirmation } from './confidentialityRules';
import { CaseSummarySection, EMPTY_VALUE } from './sections/CaseSummarySection';
import { ClosingConditionsSection } from './sections/ClosingConditionsSection';
import { ExperienceRecordSection } from './sections/ExperienceRecordSection';
import { UnfinishedTasksSection } from './sections/UnfinishedTasksSection';
import { WarningRow } from './sections/WarningRow';
import { useConfirmCaseStillConfidential } from './useConfirmCaseStillConfidential';

export interface CloseCaseModalProps {
    matterId: number;
    onCancel: () => void;
    onClosed: (matter: MatterDto) => void;
}

export function CloseCaseModal({ matterId, onCancel, onClosed }: CloseCaseModalProps) {
    const { data: check, isFetching, isError, refetch } = useGetCaseClosingCheckQuery(matterId);
    const [closeCase] = useCloseCaseMutation();
    const confirmCaseStillConfidential = useConfirmCaseStillConfidential();
    const { user } = useAuth();
    const navigateWithReturn = useNavigateWithReturnState();

    const [defaultValues] = useState<CloseCaseFormValues>(() => ({
        closedDate: formatDateStringInput(new Date()),
        xrChoice: CloseCaseXrChoice.DECIDE_LATER,
    }));
    const { control, setValue, handleSubmit } = useForm<CloseCaseFormValues>({ defaultValues });

    const [decision, setDecision] = useState<ConfidentialityDecision | null>(null);
    const [reason, setReason] = useState('');
    const [showErrors, setShowErrors] = useState(false);

    async function close(closingCheck: CaseClosingCheckDto, values: CloseCaseFormValues, clickedAt: Date) {
        const xrChoice =
            closingCheck.experienceRecord.state === 'NONE' ? values.xrChoice : CloseCaseXrChoice.DECIDE_LATER;
        try {
            const matter = await closeCase({
                matterId,
                ...buildCloseMatterRequest(
                    closingCheck.isConfidential,
                    values.closedDate ?? '',
                    decision,
                    reason,
                    xrChoice
                ),
            }).unwrap();
            successAlert(t('case-closing-modal.success', { caseNumber: closingCheck.caseNumber }));
            onClosed(matter);
            if (xrChoice === CloseCaseXrChoice.REMIND && user) {
                const state: CloseCaseTaskPrefillState = {
                    taskPrefill: buildXrTaskPrefill(closingCheck.fullName, user, clickedAt),
                };
                navigateWithReturn(`/matter/${matterId}/task/new`, { state });
            }
        } catch (error) {
            // The global API error handler stays silent for a refusal: the reloaded check shows what changed.
            if (isClosingRefusal(error)) {
                setShowErrors(true);
                refetch();
            }
        }
    }

    function handleCloseClick() {
        if (!check) {
            return;
        }
        const clickedAt = new Date();
        void handleSubmit(
            async (values) => {
                const isReasonRequired = check.isConfidentialReasonRequired;
                if (!isConfidentialityChoiceValid(check.isConfidential, decision, reason, isReasonRequired)) {
                    setShowErrors(true);
                    return;
                }
                if (
                    requiresStillConfidentialConfirmation(check.isConfidential, decision, reason) &&
                    !(await confirmCaseStillConfidential())
                ) {
                    return;
                }
                await close(check, values, clickedAt);
            },
            () => setShowErrors(true)
        )();
    }

    return (
        <>
            <ModalHeader title={t('case-closing-modal.title')} onCancel={onCancel} />
            <div className="relative min-h-24 overflow-y-auto p-6 max-sm:px-4">
                {check ? (
                    <div className="flex flex-col gap-5">
                        <p className="text-body2 m-0">
                            <FormattedMessage
                                id="case-closing-modal.intro"
                                values={{
                                    case: (
                                        <span key="case" className="inline-flex items-baseline gap-1 font-semibold">
                                            <MatterIndicators isConfidential={check.isConfidential} />
                                            {check.fullName}
                                        </span>
                                    ),
                                    client: check.clientParty?.name ?? EMPTY_VALUE,
                                }}
                            />
                        </p>
                        {!isMatterInProgress(check.matterStateTypeCode) ? (
                            <WarningRow role="alert">
                                <FormattedMessage id="case-closing-modal.not-in-progress" />
                            </WarningRow>
                        ) : isCaseClosingBlocked(check) ? (
                            <WarningRow role="alert">
                                <FormattedMessage id="case-closing-modal.blocked" />
                            </WarningRow>
                        ) : null}
                        <CaseSummarySection check={check} />
                        <DateInput<CloseCaseFormValues>
                            name="closedDate"
                            control={control}
                            label="case-closing-modal.closed-date"
                            shouldTranslateLabel
                            required
                        />
                        <ClosingConditionsSection check={check} />
                        <UnfinishedTasksSection check={check} />
                        {check.isConfidential ? (
                            <ConfidentialitySection
                                decision={decision}
                                reason={reason}
                                isReasonRequired={check.isConfidentialReasonRequired}
                                showErrors={showErrors}
                                onDecisionChange={setDecision}
                                onReasonChange={setReason}
                            />
                        ) : null}
                        <ExperienceRecordSection
                            experienceRecord={check.experienceRecord}
                            control={control}
                            setValue={setValue}
                        />
                        <p className="text-body2 m-0">
                            <FormattedMessage id="case-closing-modal.consequences" />
                        </p>
                    </div>
                ) : isError ? null : (
                    <CircularLoader />
                )}
            </div>
            <ModalFooter
                onCancel={onCancel}
                onSave={handleCloseClick}
                saveButtonTextId="case-closing-modal.close-case"
                disableSave={isCloseCaseButtonDisabled(check, isFetching)}
            />
        </>
    );
}
